import {Router} from '@angular/router';
import {Observable, Subject, Subscription} from 'rxjs';
import {ApiError, ApiResponse, FilterModel, ReportFilter} from '@spa/api/lib/common';

import {ColumnSizesEnum} from 'appShared/enums';
import {DefaultItemsPerPage} from 'appShared/widgets';
import {CacheCollectionService} from 'appShared/services';
import {FilterControlInterface} from 'appShared/interfaces';
import {BaseControl, FilterControl} from 'appShared/classes';

import {
    PagesChunkChangedEventDataInterface,
    PaginationConfigInterface, PaginationConfigRawDataInterface,
    ReportTableConfigInterface,
    ReportTableData,
    ReportTableDataRow,
} from 'appShared/components';

import {EventsService} from '../services';
import {LayoutConfigInterface} from '../components';

import {BaseComponent} from './base-component';

export abstract class BaseReportComponent<ReportInterfaceType, ReportModelType> extends BaseComponent {
    protected filterSubjectSubscription: Subscription;
    protected isFilterChanged: boolean;
    protected paginateFromPage: number;
    protected paginateToPage: number;

    public searchSubscription: Subscription;
    public filterModelSubject: Subject<FilterModel | ReportFilter>;
    public filterModel: FilterModel | ReportFilter;
    public modelsCollection: ReportModelType[];
    public filterControls: FilterControl[];
    public dataTableConfig: ReportTableConfigInterface;
    public paginationConfig: PaginationConfigInterface;

    protected constructor(
        eventsService: EventsService,
        router: Router,
        cacheCollectionService: CacheCollectionService
    ) {
        super(eventsService, router, cacheCollectionService);
        this.modelsCollection = [];
    }

    abstract getInitialFilterModel(itemsPerPage?: number): FilterModel | ReportFilter;

    abstract getFilterModel(data: { [p: string]: number | string | boolean }): FilterModel | ReportFilter;

    abstract mapFilterResults(results: ReportInterfaceType[]): ReportModelType[];

    abstract createDataSource(data: ReportModelType[]): ReportTableDataRow[];

    abstract getReportHandler(filter: FilterModel | ReportFilter): Observable<ApiResponse<ApiError[] | ReportInterfaceType[]>>;

    abstract fixPaginationAndDataSource(initialPaginationPage: number, paginateFrom: number, paginateTo: number): void;

    onDestroy(): void {
        super.onDestroy();

        if (this.filterSubjectSubscription) {
            this.filterSubjectSubscription.unsubscribe();
        }
    }

    getLayoutConfig(): LayoutConfigInterface {
        return {
            mainColumnSize: ColumnSizesEnum.Col12,
            subColumnSize: null
        };
    }

    paginateCollection<DataType>(collection: DataType[], start = 0, end = DefaultItemsPerPage): DataType[] {
        return collection.filter((item: DataType, index: number) => {
            return index >= start && index < end;
        });
    }

    onPageChanged(data: { start: number; end: number, currentPage: number }): void {
        const paginatedCollection = this.paginateCollection<ReportModelType>(
            this.modelsCollection,
            data.start,
            data.end
        );

        this.dataTableConfig.rows = this.createDataSource(paginatedCollection).map((row: ReportTableDataRow) => {
            row.dataItems.map((item: ReportTableData) => {
                if (this.dataTableConfig.hiddenColumnIds.indexOf(item.colId) >= 0) {
                    item.hidden = true;
                }
            });

            return row;
        });
        this.paginationConfig.currentPage = data.currentPage;
        const paginationData = {
            itemsPerPage: this.paginationConfig.itemsPerPage,
            currentPage: this.paginationConfig.currentPage,
            pagesChunk: this.paginationConfig.pagesChunk,
            totalItems: this.paginationConfig.totalItems
        } as PaginationConfigRawDataInterface;
        this.cacheCollectionService.addItem(this.componentStorageKeyProvider.getPaginationConfigKey(),
            paginationData);

    }

    filterResultSuccessHandler(
        initialPaginationPage: number,
        paginateFrom: number,
        paginateTo: number,
        response: ApiResponse<ApiError[] | ReportInterfaceType[]>
    ): void {

        if (response.data !== null) {
            const data = response.data as ReportInterfaceType[];

            this.modelsCollection = this.mapFilterResults(data);

            this.fixPaginationAndDataSource(
                initialPaginationPage,
                paginateFrom,
                paginateTo
            );
        } else {
            this.modelsCollection =
                this.dataTableConfig.rows = [];

            this.paginationConfig.configChanged.emit({initialPage: null, reset: true});
        }
        const shownFilters = [];
        const filterObject = this.filterControls.reduce(
            (reducedObject: { [key: string]: number | string | boolean }, filterControl: FilterControlInterface) => {
                filterControl.controls.map((control: BaseControl) => {
                    reducedObject[control.name] = control.value;
                });
                if (filterControl.shown) {
                    shownFilters.push(filterControl.name);
                }

                return reducedObject;
            }, {});
        this.cacheCollectionService.addItem(this.componentStorageKeyProvider.getFilterValueKey(), filterObject);
        this.cacheCollectionService.addItem(this.componentStorageKeyProvider.getShownFilterKey(), shownFilters);
        this.cacheCollectionService.addItem(this.componentStorageKeyProvider.getFilterModelKey(), this.filterModel);
        const paginationData = {
            itemsPerPage: this.paginationConfig.itemsPerPage,
            currentPage: this.paginationConfig.currentPage,
            pagesChunk: this.paginationConfig.pagesChunk,
            totalItems: this.paginationConfig.totalItems
        } as PaginationConfigRawDataInterface;
        this.cacheCollectionService.addItem(this.componentStorageKeyProvider.getPaginationConfigKey(), paginationData);
    }

    onPagesChunkChanged(data: PagesChunkChangedEventDataInterface): void {
        const filterObject = this.filterControls.reduce(
            (reducedObject: { [key: string]: number | string | boolean }, filterControl: FilterControlInterface) => {
                filterControl.controls.map((control: BaseControl) => {
                    reducedObject[control.name] = control.value;
                });

                return reducedObject;
            }, {});

        this.filterModel = this.getFilterModel(filterObject);

        this.paginateToPage = data.end;
        this.paginateFromPage = data.start;
        this.paginationConfig.pagesChunk = data.chunk;
        this.paginationConfig.currentPage = data.page;

        this.filterModel.setPageNumber(data.chunk);

        if (!data.paginateCurrent) {
            this.filterModelSubject.next(this.filterModel);
        } else {
            this.modelsCollection = this.modelsCollection.slice(data.currentIndex, this.modelsCollection.length);

            this.fixPaginationAndDataSource(this.paginationConfig.currentPage, data.start, data.end);
        }
        const paginationData = {
            itemsPerPage: this.paginationConfig.itemsPerPage,
            currentPage: this.paginationConfig.currentPage,
            pagesChunk: this.paginationConfig.pagesChunk,
            totalItems: this.paginationConfig.totalItems
        } as PaginationConfigRawDataInterface;
        this.cacheCollectionService.addItem(this.componentStorageKeyProvider.getPaginationConfigKey(),
            paginationData);
    }
}
